import React, { useState, useEffect } from 'react';
import { Trans } from 'react-i18next';
import { CSVLink } from 'react-csv';
import PropTypes from 'prop-types';
import { publishErrorNotification } from '@nbx/frontend-helpers/publish';
import cs from 'classnames';
import Spinner from 'components/shared/Spinner';
import Row from 'components/shared/Row';
import Column from 'components/shared/Column';
import Text from 'components/shared/Text';
import ButtonText from 'components/shared/ButtonText';
import ButtonPrimary from 'components/shared/ButtonPrimary';
import Select from 'components/shared/Select';
import useAssets from 'hooks/useAssets';
import ERRORS from 'constants/errors';
import { isWebApp } from 'helpers/platform';
import { useAnnualReport } from '../../services';

import styles from './styles.module.scss';

const BASE_YEAR = 2020; // First year with customers
const CURRENT_YEAR = new Date().getFullYear();

const headers = year => [
  { label: `NBX Annual trade report ${year} v1.0`, key: 'none' },
  { label: 'Timestamp', key: 'formattedDate' },
  { label: 'Type', key: 'type' },
  { label: 'ID', key: 'id' },
  { label: 'In', key: 'inAmount' },
  { label: 'In-Currency', key: 'inCurrency' },
  { label: 'Out', key: 'outAmount' },
  { label: 'Out-Currency', key: 'outCurrency' },
  { label: 'Fee', key: 'fee' },
  { label: 'Fee-Currency', key: 'feeCurrency' },
  { label: 'Market', key: 'market' },
  { label: 'Notes', key: 'notes' }
];

function AnnualReport(props) {
  const assets = useAssets();
  const csvLinkRef = React.useRef(null);
  const [yearOptions, setYearOptions] = React.useState({});
  const [year, setYear] = React.useState(`${CURRENT_YEAR - 1}`);

  const [isLoading, setIsLoading] = useState(false);
  const [reportData, setReportData] = useState();

  const { data, loading, error } = useAnnualReport(!isLoading, props.accountId, year, assets.data);

  useEffect(() => {
    if (loading) return;
    setIsLoading(false);
    if (error) {
      publishErrorNotification({ message: ERRORS.annualReport });
      console.error('Error while fetching accounts annual report data: ', error);
    } else if (data) setReportData(data);
  }, [data, error, loading]);

  useEffect(() => {
    if (reportData && csvLinkRef.current) csvLinkRef.current.link.click();
  }, [reportData, csvLinkRef]);

  useEffect(() => {
    const yearsInRange = {};
    for (let i = 0; i < CURRENT_YEAR - BASE_YEAR + 1; i++) {
      yearsInRange[BASE_YEAR + i] = `${BASE_YEAR + i}`;
    }
    setYearOptions(yearsInRange);
  }, []);

  const handleOnClick = () => setIsLoading(true);

  const handleChangeYear = e => {
    setReportData(null);
    setYear(e.target.value);
  };

  const renderDownloadButtons = () => {
    if (reportData) {
      return (
        <CSVLink
          disabled={!reportData || isLoading}
          data={reportData}
          headers={headers(year)}
          filename={`NBX_annual_report_${year}.csv`}
          ref={csvLinkRef}
          separator=";">
          <ButtonPrimary disabled={isLoading} styles={[styles.selectAndButton]}>
            <Trans i18nKey="ann_report_download">Download Annual Report</Trans>
          </ButtonPrimary>
        </CSVLink>
      );
    }
    return (
      <ButtonPrimary onClick={handleOnClick} disabled={isLoading} styles={[styles.selectAndButton]}>
        <Trans i18nKey="ann_report_download">Download Annual Report</Trans>
      </ButtonPrimary>
    );
  };

  if (!isWebApp()) {
    return (
      <div className={styles.mobileMessageWrapper}>
        <Text weight="bold" styles={[styles.headerColumn]}>
          <Trans i18nKey="annual_report">Annual Report</Trans>
        </Text>
        <Text muted>
          <Trans i18nKey="orders_annual_report_no_mobile">
            Please log in to NBX on a computer to export your annual tax report.
          </Trans>
        </Text>
      </div>
    );
  }

  if (assets.loading) return <Spinner delayed />;

  if (assets.error)
    return (
      <div className={styles.errorContainer}>
        <Text centered styles={[styles.errorText]}>
          <Trans i18nKey="error_title_generic" />
        </Text>
        <ButtonText onClick={assets.refetch}>
          <Trans i18nKey="common_please_try_again" />
        </ButtonText>
      </div>
    );

  return (
    <div className={cs(styles.annualReportWrapper)}>
      {isLoading && (
        <div className={cs(styles.loadingOverlay)}>
          <div className={cs(styles.pleaseWaitText)}>
            <Text>
              <Trans i18nKey="ann_report_generating_please_wait">
                Generating your report. This might take a long time, please wait.
              </Trans>
            </Text>
          </div>
          <Spinner staticPos />
        </div>
      )}

      <Row styles={[styles.annualReportRow]} startAligned>
        <Column lg={3} sm={12}>
          <Text weight="bold" styles={[styles.headerColumn]}>
            <Trans i18nKey="annual_report">Annual Report</Trans>
          </Text>
        </Column>
        <Column centered lg={6} sm={12}>
          <Text>
            <Trans
              i18nKey="ann_report_descr"
              components={{
                GuideLink: (
                  <ButtonText href="https://www.skatteetaten.no/en/person/taxes/get-the-taxes-right/shares-and-securities/about-shares-and-securities/digital-currency/" />
                ),
                KryptosekkenLink: <ButtonText href="https://www.kryptosekken.no" />,
                DivlyLink: <ButtonText href="https://divly.com/" />,
                DivlyLinkCountries: <ButtonText href="https://divly.com/en/countries" />
              }}
            />
          </Text>
        </Column>
        <Column endAligned lg={3} sm={12} styles={[styles.downloadButtonColumn]}>
          <Select
            id="report_year"
            name="year"
            value={`${year}`}
            onChange={handleChangeYear}
            options={yearOptions}
            wrapperStyles={[styles.selectAndButton]}
            labelStyles={[styles.hiddenLabel]}
          />
          {renderDownloadButtons()}
        </Column>
      </Row>
    </div>
  );
}

AnnualReport.propTypes = {
  accountId: PropTypes.string.isRequired
};

export default AnnualReport;
